import React, { useState, useEffect } from "react";
import { Field, FieldArray, ErrorMessage } from "formik";
import { ReactstrapInput } from "reactstrap-formik";
import * as Yup from "yup";
import { Row, Col, Button } from "reactstrap";
import CrudEdit from "../Crud/Edit";
import ElevatorCompanyService from "core/api/ElevatorCompanyService";
import SettingsManagementService from "core/api/SettingsManagementService";
import Address from "../Shared/Address"
import Contact from "../Shared/Contact"
import { paths } from "utils/PathService";
import { cleanPhoneNumberWithoutCountryCode } from "components/Phone/PhoneFunctions";

const DIDLineType = {
  Emergency: "Emergency",
  CustomerService: "CustomerService"
};

const DID_LINE_TYPES = [
  { value: DIDLineType.Emergency, label: "Emergency" },
  { value: DIDLineType.CustomerService, label: "Customer Service" }
];

const ElevatorCompanySchema = Yup.object().shape({
  name: Yup.string().required("Required"),
  line1: Yup.string().required("Required"),
  line2: Yup.string(),
  city: Yup.string().required("Required"),
  state: Yup.string()
    .length(2, "Invalid state")
    .uppercase("Must be uppercase")
    .required("Required"),
  postalCode: Yup.string().required("Required"),
  country: Yup.string()
    .length(3, "3 letter country initials")
    .required("Required"),
  sendSummaryReport: Yup.boolean(),
  summaryReportDeliveryMode: Yup.string().nullable().when('sendSummaryReport', {
    is: true,
    then: Yup.string().required("*Select a report setting"),
    otherwise: Yup.string()
  }),
  communicateThroughSMS: Yup.boolean(),
  smsDID: Yup.string()
    .transform((value) => value ? cleanPhoneNumberWithoutCountryCode(value) : '')
    .matches(/^(\d{10})?$/, "Must be a valid phone number"),
  callTimeoutSetting: Yup.number()
    .integer("Must be a whole number")
    .min(1, "Must be at least 1 minute")
    .max(300, "Cannot exceed 300 minutes (5 hours)")
    .required("Required"),
  contacts: Yup.array().of(
    Yup.object().shape({
      contactName: Yup.string().required("Required"),
      phone: Yup.string().required("Required")
        .transform((value) => cleanPhoneNumberWithoutCountryCode(value))
        .matches(/^(911|\d{10})$/, "Must be a valid phone number"),
      email: Yup.string().email("invalid email"),
      defaultContact: Yup.boolean()
    })
  ).test("defaultContact", "Missing default contact", (contacts) => contacts.some(contact => contact.defaultContact)),
  dids: Yup.array().of(
    Yup.object().shape({
      phoneNumber: Yup.string().required("Required")
        .transform((value) => value?.replace(/\D/g, ''))
        .matches(/^\d{10}$/, "Must be a valid 10-digit phone number"),
      comments: Yup.string(),
      lineType: Yup.string().required("Required")
    })
  )
});

const Edit = () => {
  const [defaultTimeout, setDefaultTimeout] = useState(null);
  const [settingsLoaded, setSettingsLoaded] = useState(false);

  useEffect(() => {
    async function fetchDefaults() {
      const setting = await SettingsManagementService.getByName('DefaultDispatchTimeoutMinutes');
      setDefaultTimeout(setting?.value);

      setSettingsLoaded(true);
    }
    fetchDefaults();
  }, []);

  if (!settingsLoaded) return <>Loading...</>;

  const initialData = {
    id: null,
    name: "",
    dids: [{ phoneNumber: "", comments: "", lineType: DIDLineType.Emergency }],
    line1: "",
    line2: "",
    city: "",
    state: "NY",
    postalCode: "",
    country: "USA",
    sendSummaryReport: false,
    summaryReportDeliveryMode: "individual",
    communicateThroughSMS: false,
    smsDID: "",
    callTimeoutSetting: defaultTimeout,
    contacts: [{
      contactName: "",
      phone: "",
      email: "",
      availableForEmergencyCalls: false,
      sendAlerts: false,
      notifyWhen911Called: false,
      defaultContact: true,
      smsSummaryCallReport: false,
      emailSummaryCallReport: false,
      smsWhenHelpNeeded: false,
      emailWhenHelpNeeded: false,
      smsForCustomerServiceReport: false,
      emailForCustomerServiceReport: false,
      phoneNotes: "",
      phoneExtension: "",
      loggedOnToReceiveSMS: false,
      callCenterMonitoringContact: false,
      smsWhenContactStatusChanges: false,
      smsWhenNoAvailableContacts: false
    }],

  };


  return (
    <>
      <CrudEdit
        title="Elevator Company"
        initData={initialData}
        crudService={ElevatorCompanyService}
        validationSchema={ElevatorCompanySchema}
        getRedirectPath={(id) => { return paths.elevatorCompany.view(id) }}
        getData={async (id) => {
          const data = await ElevatorCompanyService.get(id);
          return {
            ...data,
            callTimeoutSetting: data.callTimeoutSetting || defaultTimeout,
            dids: data.dids?.map(did => ({
              ...did,
              lineType: did.lineType || DIDLineType.Emergency
            }))
          };
        }}
        transformValues={(values) => ({
          ...values,
          smsDID: values.smsDID ? cleanPhoneNumberWithoutCountryCode(values.smsDID) : '',
          dids: values.dids?.map(did => ({
            ...did,
            phoneNumber: did.phoneNumber?.replace(/\D/g, ''),
            lineType: did.lineType || DIDLineType.Emergency
          })),
          contacts: values.contacts?.map(contact => ({
            ...contact,
            phone: cleanPhoneNumberWithoutCountryCode(contact.phone)
          }))
        })}
      >

        <Row>
          <Col md="4">
            <Field
              type="text"
              name="name"
              label="Company Name"
              component={ReactstrapInput}
            />
          </Col>
        </Row>

        <Address component={ReactstrapInput} />


        <Row>
          <Col md="4">
            <Field
              type="text"
              name="smsDID"
              label="SMS DID"
              component={ReactstrapInput}
            />
          </Col>
          <Col md="4">
            <Field
              type="number"
              name="callTimeoutSetting"
              label="Dispatch Timeout (minutes)"
              component={ReactstrapInput}
            />
          </Col>
        </Row>

        <Row className="mt-2">
          <Field type="checkbox" name={`sendSummaryReport`}>
            {({ field, form }) =>
              <>
                <Col md="4">
                  <label style={{ fontSize: 14 }}>
                    <input type="checkbox" {...field} />
                    {"  "}Email Summary Call Report When Call is Completed</label>
                </Col>

                {field.value && (
                  <>
                    <Col >
                      <label style={{ fontSize: 14, paddingRight: 20 }}>Report Settings:</label>

                      <label style={{ fontSize: 14, paddingRight: 10 }}>
                        <Field type="radio" name={`summaryReportDeliveryMode`} value="individual"
                        />
                        {"  "}Send individual email to each contact
                      </label>
                      <label style={{ fontSize: 14 }}>
                        <Field type="radio" name={`summaryReportDeliveryMode`} value="grouped"
                        />
                        {"  "}Send one email with all contacts included
                      </label>
                    </Col> 

                    {typeof form.errors.summaryReportDeliveryMode === 'string' && form.touched.summaryReportDeliveryMode && (
                      <ErrorMessage name={`summaryReportDeliveryMode`} component="div" className="invalid-feedback" style={{ display: "block", fontSize: 16 }} />)}

                  </>
                )}



              </>
            }
          </Field>
        </Row>

        <Row style={{ marginTop: 10 }}>
          <Col md="3">
            <label style={{ fontSize: 14 }}>
              <Field
                type="checkbox"
                name="communicateThroughSMS"
              />
              {"   Communicate through SMS"}
            </label>
          </Col>
        </Row>

        <Row>
          <FieldArray name="dids">
            {({ form: { values, errors, touched }, push, remove }) => (
              <div>
                <div>
                  <p style={{ paddingBottom: 0, marginBottom: 0, marginTop: 20, fontSize: 18, fontWeight: "bold" }}>DIDs:</p>
                  <Button color="info" onClick={() => push({ phoneNumber: "", comments: "", lineType: DIDLineType.Emergency })}>
                    Add DID
                  </Button>
                </div>
                {values.dids?.map((did, index) => (
                  <Row key={index} className="mb-2">
                    <Col md="2">
                      <Field
                        type="text"
                        name={`dids.${index}.phoneNumber`}
                        label="Phone Number"
                        component={ReactstrapInput}
                      />
                      {typeof errors.dids?.[index]?.phoneNumber === 'string' && touched.dids?.[index]?.phoneNumber && (
                      <ErrorMessage name={`dids.${index}.phoneNumber`} component="div" className="invalid-feedback" style={{ display: "block" }} />
                      )}
                    </Col>

                    <Col md="2">
                      <div className="form-group">
                        <label>Line Type</label>
                        <Field as="select" name={`dids.${index}.lineType`} className="form-control">
                          {DID_LINE_TYPES.map(lineType => (
                            <option key={lineType.value} value={lineType.value}>
                              {lineType.label}
                            </option>
                          ))}
                        </Field>
                        {typeof errors.dids?.[index]?.lineType === 'string' && touched.dids?.[index]?.lineType && (
                          <ErrorMessage name={`dids.${index}.lineType`} component="div" className="invalid-feedback" style={{ display: "block" }} />
                        )}
                      </div>
                    </Col>
                    <Col md="4">
                      <Field
                        type="text"
                        name={`dids.${index}.comments`}
                        label="Comments"
                        component={ReactstrapInput}
                      />
                    </Col>
                    <Col md="2" className="d-flex align-items-center">
                      <Button color="primary" outline size="sm" onClick={() => remove(index)}>
                        Remove
                      </Button>
                    </Col>
                  </Row>
                ))}

              </div>
            )}
          </FieldArray>
        </Row>

        <p style={{ paddingBottom: 0, marginBottom: 0, marginTop: 20, fontSize: 18, fontWeight: "bold" }}>Contacts:</p>

        <Row>
          <FieldArray name="contacts">
            {({ form: { values, setFieldValue, errors, touched }, push, remove }) => {
              return (
                <div>
                  {values.contacts.map((contact, index) => (
                    <div key={index}>
                      <Contact component={ReactstrapInput} fieldNamePrefix={`contacts.${index}.`} remove={() => remove(index)} contactType="elevatorCompany"
                        setDefaultContact={(value) => {
                          if (value) { values.contacts.forEach((contact, i) => { setFieldValue(`contacts.${i}.defaultContact`, false) }); }
                          setFieldValue(`contacts.${index}.defaultContact`, value);
                        }} />
                    </div>

                  ))}

                  {typeof errors.contacts === 'string' && touched.contacts && (
                    <ErrorMessage name={`contacts`} component="div" className="invalid-feedback" style={{ display: "block", fontSize: 16 }} />)}


                  <Button color="info" onClick={() => {
                    push({
                      contactName: "", phone: "", email: "", availableForEmergencyCalls: false, sendAlerts: false, notifyWhen911Called: false, defaultContact: false,
                      smsSummaryCallReport: false, emailSummaryCallReport: false,
                      smsWhenHelpNeeded: false, emailWhenHelpNeeded: false,
                      smsForCustomerServiceReport: false, emailForCustomerServiceReport: false,
                      callCenterMonitoringContact: false,
                      phoneNotes: "", phoneExtension: "", loggedOnToReceiveSMS: false, smsWhenContactStatusChanges: false, smsWhenNoAvailableContacts: false
                    });
                  }} >Add Contact</Button>
                </div>)
            }}
          </FieldArray>
        </Row>

      </CrudEdit>
    </>
  );
};

export default Edit;
